import { SAMPLE_INJECTIONS_LIMIT, SEQUENCE_INJECTIONS_LIMIT } from '@/constants/samples/limits';
import { Nullable } from '@/types/utility';

export enum LimitType {
  SAMPLE = 'sample',
  SEQUENCE = 'sequence',
  BOTH = 'both',
  NONE = 'none',
}

type DraftRow = {
  id?: string | number;
  type?: string;
  repeat?: number | string | null;
  sample?: string | number | null;
};

type ExcludePredicate = (draft: DraftRow) => boolean;

type CountOptions = {
  excludePredicate?: ExcludePredicate;
};

type RemainingCapacity = {
  sample: number;
  sequence: number;
  maxAllowed: number;
};

/**
 * Result of evaluating injection limits for a repeat value.
 * - normalizedRepeat: The input repeat value after basic normalization (converts to number, handles invalid/negative values)
 * - clampedRepeat: The normalized repeat value clamped to not exceed available capacity limits
 * - limitedBy: Indicates which limit constraint was applied (SAMPLE, SEQUENCE, BOTH, or null if no clamping occurred)
 */
type LimitEvaluation = RemainingCapacity & {
  normalizedRepeat: number;
  clampedRepeat: number;
  limitedBy: Nullable<LimitType>;
};

const normalizeRepeat = (value: DraftRow['repeat']): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return parsed < 0 ? 0 : parsed;
};

const shouldExclude = (draft: DraftRow, predicate?: ExcludePredicate) => {
  return predicate ? predicate(draft) : false;
};

export const countSampleInjections = (
  rows: DraftRow[],
  sampleId: DraftRow['sample'],
  options: CountOptions = {},
): number => {
  return rows.reduce((counter, row) => {
    if (shouldExclude(row, options.excludePredicate)) {
      return counter;
    }

    if (row.sample !== sampleId) {
      return counter;
    }

    return counter + normalizeRepeat(row.repeat);
  }, 0);
};

const countTotalInjections = (rows: DraftRow[], options: CountOptions = {}): number => {
  return rows.reduce((counter, row) => {
    if (shouldExclude(row, options.excludePredicate)) {
      return counter;
    }

    return counter + normalizeRepeat(row.repeat);
  }, 0);
};

export const calculateRemainingCapacity = (
  rows: DraftRow[],
  sampleId: DraftRow['sample'],
  options: CountOptions = {},
): RemainingCapacity => {
  const scheduledForSample = sampleId == null ? 0 : countSampleInjections(rows, sampleId, options);
  const totalScheduled = countTotalInjections(rows, options);

  const remainingForSample = SAMPLE_INJECTIONS_LIMIT - scheduledForSample;
  const remainingForSequence = SEQUENCE_INJECTIONS_LIMIT - totalScheduled;
  const maxAllowed = Math.max(0, Math.min(remainingForSample, remainingForSequence));

  return {
    sample: remainingForSample,
    sequence: remainingForSequence,
    maxAllowed,
  };
};

export const evaluateRepeatLimits = (params: {
  rows: DraftRow[];
  sampleId: DraftRow['sample'];
  repeat: DraftRow['repeat'];
  excludePredicate?: ExcludePredicate;
}): LimitEvaluation => {
  const { rows, sampleId, repeat, excludePredicate } = params;
  const normalizedRepeat = normalizeRepeat(repeat);
  const capacity = calculateRemainingCapacity(rows, sampleId, { excludePredicate });
  const clampedRepeat = Math.min(normalizedRepeat, capacity.maxAllowed);

  let limitedBy: LimitEvaluation['limitedBy'] = null;
  if (normalizedRepeat > clampedRepeat) {
    if (capacity.sample < capacity.sequence) {
      limitedBy = LimitType.SAMPLE;
    } else if (capacity.sequence < capacity.sample) {
      limitedBy = LimitType.SEQUENCE;
    } else {
      limitedBy = LimitType.BOTH;
    }
  }

  return {
    normalizedRepeat,
    clampedRepeat,
    limitedBy,
    ...capacity,
  };
};
